import { useState, useEffect } from 'react';
import { useAuth } from './use-auth';
import { supabaseHelpers } from '@/lib/supabase';

// Custom event for language changes
const LANGUAGE_CHANGE_EVENT = 'vyzle-chat-language-change';

export function useUserLanguage() {
  const { user } = useAuth();
  const [userLanguage, setUserLanguage] = useState<string>('en');
  const [loading, setLoading] = useState(true);

  // Function to get the current language from localStorage
  const getCurrentLanguage = () => {
    const savedLanguage = localStorage.getItem('vyzle-chat-language');
    if (savedLanguage) {
      return savedLanguage;
    }
    // Fallback to browser language
    return navigator.language.split('-')[0];
  };

  useEffect(() => {
    const loadUserLanguage = async () => {
      try {
        // localStorage is the source of truth for language preference
        const savedLanguage = getCurrentLanguage();
        setUserLanguage(savedLanguage);

        // If user is authenticated, sync preferences TO Supabase (not FROM)
        if (user?.uid) {
          try {
            const userData = await supabaseHelpers.getUser(user.uid);
            // If DB value differs from localStorage, update DB to match localStorage
            if (userData?.preferred_language !== savedLanguage) {
              console.log('Syncing language preference to Supabase:', savedLanguage);
              await supabaseHelpers.updateUser(user.uid, { preferred_language: savedLanguage });
            }
          } catch (error) {
            console.warn('Failed to sync user language to Supabase:', error);
          }
        }
      } catch (error) {
        console.error('Error loading user language:', error);
        // Fallback to browser language
        const browserLang = navigator.language.split('-')[0];
        setUserLanguage(browserLang);
        localStorage.setItem('vyzle-chat-language', browserLang);
      } finally {
        setLoading(false);
      }
    };

    loadUserLanguage();
  }, [user]);

  // Listen for custom language change events
  useEffect(() => {
    const handleLanguageChange = (e: CustomEvent) => {
      if (e.detail && e.detail.language) {
        setUserLanguage(e.detail.language);
      }
    };

    window.addEventListener(LANGUAGE_CHANGE_EVENT, handleLanguageChange as EventListener);

    return () => {
      window.removeEventListener(LANGUAGE_CHANGE_EVENT, handleLanguageChange as EventListener);
    };
  }, []);

  const updateLanguage = async (language: string) => {
    try {
      console.log('Updating language to:', language);
      setUserLanguage(language);
      // Update localStorage immediately for responsive UI
      localStorage.setItem('vyzle-chat-language', language);

      // Dispatch custom event to notify other components
      window.dispatchEvent(new CustomEvent(LANGUAGE_CHANGE_EVENT, {
        detail: { language }
      }));

      // Update Supabase if user is authenticated
      if (user?.uid) {
        await supabaseHelpers.updateUser(user.uid, { preferred_language: language });
      }
    } catch (error) {
      console.error('Failed to update language preference:', error);
      // Revert localStorage if Supabase update failed
      const savedLanguage = localStorage.getItem('vyzle-chat-language');
      if (savedLanguage && savedLanguage !== language) {
        setUserLanguage(savedLanguage);
      }
      throw error;
    }
  };

  return { userLanguage, updateLanguage, loading };
}